import React, {useEffect, useLayoutEffect, useRef} from 'react';
import {EChartOption, getInstanceByDom, init} from 'echarts';
// @ts-ignore echarts locale bundle has no types in this project
import langPTBr from 'echarts/lib/i18n/langPT-br';

export interface ManagementOverviewAChartXProps {
    option: EChartOption;
    chartId?: string;
    onChartReady?: (chartId: string) => void;
    style?: React.CSSProperties;
}

const ManagementOverviewAChartX: React.FC<ManagementOverviewAChartXProps> = ({
    option,
    chartId,
    onChartReady,
    style,
}) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const notifiedRef = useRef(false);
    const onChartReadyRef = useRef(onChartReady);

    useEffect(() => {
        onChartReadyRef.current = onChartReady;
    }, [onChartReady]);

    useEffect(() => {
        notifiedRef.current = false;
    }, [chartId, option]);

    useLayoutEffect(() => {
        const el = chartRef.current;
        if (el === null) {
            return;
        }

        let chart = getInstanceByDom(el);
        if (!chart) {
            const width = Math.max(el.clientWidth, 1);
            const height = Math.max(el.clientHeight, 1);
            chart = init(el, undefined, {
                locale: langPTBr,
                renderer: 'canvas',
                width,
                height,
            });
        }

        const handleFinished = () => {
            if (notifiedRef.current || !chartId || !onChartReadyRef.current) {
                return;
            }
            notifiedRef.current = true;
            onChartReadyRef.current(chartId);
        };

        const resizeChart = () => {
            const nextWidth = el.clientWidth;
            const nextHeight = el.clientHeight;
            if (!nextWidth || !nextHeight) {
                return;
            }
            chart.resize({ width: nextWidth, height: nextHeight });
        };

        chart.off('finished');
        chart.on('finished', handleFinished);
        chart.setOption(option, true, false);
        resizeChart();
        const frameId = window.requestAnimationFrame(resizeChart);

        const observer =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resizeChart) : null;
        observer?.observe(el);
        window.addEventListener('resize', resizeChart);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', resizeChart);
            observer?.disconnect();
            chart.off('finished', handleFinished);
            chart.dispose();
        };
    }, [option, chartId]);

    return (
        <div
            ref={chartRef}
            data-chart-id={chartId}
            style={{
                width: '100%',
                height: '100%',
                ...style,
            }}
        />
    );
};

export default ManagementOverviewAChartX;
